import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiUsers, FiSend, FiTrash2, FiMail, FiDownload, FiEye, FiCode, FiCheck, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminWaitlist = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const [template, setTemplate] = useState(null);
  const [viewMode, setViewMode] = useState('preview');
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchSubscribers();
    fetchTemplate();
  }, []);

  const fetchSubscribers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('waitlist')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setSubscribers(data);
    setLoading(false);
  };

  const fetchTemplate = async () => {
    const { data } = await supabase
      .from('email_templates')
      .select('*')
      .eq('name', 'waitlist_blast')
      .maybeSingle();
    if (data) setTemplate(data);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this subscriber from the waitlist?')) return;
    await supabase.from('waitlist').delete().eq('id', id);
    setSubscribers(prev => prev.filter(s => s.id !== id));
  };

  const handleSaveTemplate = async () => {
    if (!template) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({
          subject: template.subject,
          html_content: template.html_content,
          updated_at: new Date().toISOString()
        })
        .eq('id', template.id);
      if (error) throw error;
      alert('Waitlist email template saved!');
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendBlast = async () => {
    setShowConfirm(false);
    setSending(true);
    setBlastResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: { waitlist_blast: true },
        headers: { Authorization: `Bearer ${session?.access_token}` }
      });
      if (error) throw error;
      setBlastResult(data);
    } catch (err) {
      setBlastResult({ success: false, message: err.message });
    } finally {
      setSending(false);
    }
  };

  const handleExportCSV = () => {
    const csv = [
      'Email,Subscribed,Joined',
      ...subscribers.map(s => `${s.email},${s.subscribed},${new Date(s.created_at).toLocaleDateString()}`)
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeCount = subscribers.filter(s => s.subscribed).length;

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <SafeIcon icon={FiUsers} className="text-brand-gold w-6 h-6" />
          <h2 className="text-2xl font-serif text-gray-800">Waitlist</h2>
        </div>
        <p className="text-sm text-gray-500">
          Manage subscribers waiting for your shop launch. Send announcements when you're ready to go live.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Total Subscribers</p>
          <p className="text-3xl font-serif font-bold text-brand-darkSage">{subscribers.length}</p>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Active</p>
          <p className="text-3xl font-serif font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Unsubscribed</p>
          <p className="text-3xl font-serif font-bold text-red-500">{subscribers.length - activeCount}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Subscribers List */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <h3 className="font-bold text-gray-800 uppercase tracking-wider text-xs">Subscribers</h3>
            <button
              onClick={handleExportCSV}
              disabled={subscribers.length === 0}
              className="flex items-center gap-2 text-xs font-bold text-brand-darkSage hover:text-brand-gold transition-colors disabled:opacity-40"
            >
              <SafeIcon icon={FiDownload} size={14} /> Export CSV
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : subscribers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <SafeIcon icon={FiUsers} className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No subscribers yet.</p>
              <p className="text-xs mt-1">People will appear here when they join the waitlist.</p>
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
              {subscribers.map((sub) => (
                <div key={sub.id} className="flex items-center justify-between px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{sub.email}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Joined {new Date(sub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      sub.subscribed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>
                      {sub.subscribed ? 'Active' : 'Unsubscribed'}
                    </span>
                    <button
                      onClick={() => handleDelete(sub.id)}
                      className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <SafeIcon icon={FiTrash2} size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Email Blast */}
        <div className="space-y-6">
          {/* Blast Controls */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <SafeIcon icon={FiSend} className="text-brand-gold w-5 h-5" />
              <h3 className="font-bold text-gray-800 uppercase tracking-wider text-xs">Email Blast</h3>
            </div>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              Send an announcement email to all <strong className="text-brand-darkSage">{activeCount}</strong> active subscribers. 
              Edit the template below before sending.
            </p>

            {blastResult && (
              <div className={`p-4 rounded-xl text-sm mb-4 flex items-start gap-2 ${
                blastResult.success ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
              }`}>
                <SafeIcon icon={blastResult.success ? FiCheck : FiAlertCircle} className="mt-0.5 flex-shrink-0" size={16} />
                <div>
                  <p className="font-bold">{blastResult.message}</p>
                  {blastResult.sent !== undefined && (
                    <p className="text-xs mt-1 opacity-80">
                      Sent: {blastResult.sent} | Failed: {blastResult.failed} | Total: {blastResult.total}
                    </p>
                  )}
                </div>
              </div>
            )}

            {showConfirm ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-amber-800 mb-2">
                  ⚠️ Confirm: Send to {activeCount} subscribers?
                </p>
                <p className="text-xs text-amber-600 mb-3">This action cannot be undone. Each subscriber will receive one email.</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleSendBlast}
                    disabled={sending}
                    className="bg-brand-darkSage text-white px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-brand-gold transition-colors disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Yes, Send Now'}
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="bg-gray-200 text-gray-600 px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={sending || activeCount === 0}
                className="w-full bg-brand-darkSage text-white py-3 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSend} size={14} />
                {sending ? 'Sending Blast...' : `Send to ${activeCount} Subscribers`}
              </button>
            )}
          </div>

          {/* Template Editor */}
          {template && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold text-gray-800 uppercase tracking-wider text-xs flex items-center gap-2">
                  <SafeIcon icon={FiMail} className="text-brand-gold" size={14} /> Blast Template
                </h3>
                <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setViewMode('code')}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${
                      viewMode === 'code' ? 'bg-brand-darkSage text-white' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={FiCode} size={10} /> Code
                  </button>
                  <button
                    onClick={() => setViewMode('preview')}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${
                      viewMode === 'preview' ? 'bg-brand-darkSage text-white' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={FiEye} size={10} /> Preview
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Subject Line</label>
                  <input
                    type="text"
                    value={template.subject}
                    onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-brand-gold font-semibold"
                  />
                </div>

                {viewMode === 'code' ? (
                  <textarea
                    value={template.html_content}
                    onChange={(e) => setTemplate({ ...template, html_content: e.target.value })}
                    className="w-full min-h-[350px] border border-gray-200 rounded-lg p-4 text-xs font-mono bg-gray-50 outline-none focus:border-brand-gold"
                  />
                ) : (
                  <div
                    className="w-full min-h-[350px] border border-gray-200 rounded-lg p-4 bg-white overflow-auto"
                    dangerouslySetInnerHTML={{ __html: template.html_content }}
                  />
                )}

                <button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="bg-brand-darkSage text-white px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-brand-gold transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminWaitlist;